'use client';

/**
 * The `marketFigure` field's editor — **one popover holding the whole figure**
 * (VALE-1008, `components/attributes/CLAUDE.md` §3).
 *
 * *What the number is, where it came from, and whether Vale is still allowed to
 * move it.* Three facts that are useless apart, so they open together and commit
 * together, the way `valueWithConfirmation` opens a figure and its provenance
 * rather than spending two rows on them.
 *
 * ## Anatomy, and why each piece is where it is
 *
 * · **Provenance first, and read-only.** The market price, the exchange and the
 *   date it was fetched are what a reader opening a share figure most often came
 *   to check, and none of it is theirs to edit. Putting it above the controls
 *   answers the question without them touching anything. It is also the whole of
 *   what the retired ticker strip carried, which is why dropping that strip loses
 *   nothing: the strip stated it beside a `Ticker` row that already named the
 *   security, and this states it beside the figure it actually qualifies.
 * · **The manual-entry toggle is the treatment's own segmented boolean**, the
 *   same control the rows around it use, and the same argument [ATTR-8] made: an
 *   unticked box cannot distinguish *no* from *nobody answered*, and on the
 *   question of who owns a figure in a legal record that ambiguity is the whole
 *   point.
 * · **The fields are revealed by the answer, not disabled by it** — the rule
 *   `AttributeValueConfirmationEditor` already follows. A greyed-out input still
 *   occupies the eye and still asks to be clicked.
 * · **Both the price and the value are editable, and only one of them derives.**
 *   Editing the price recomputes the value through the spec's `deriveValue`;
 *   editing the value leaves the price alone. That asymmetry is the flag-off
 *   form's shipped behaviour (`onCurrentPrice` derives, `onCurrentValue` does
 *   not), and it is the right way round: a reader who types a total is stating
 *   the total, not implying a new unit price.
 *
 * ## A figure that never resyncs shows no toggle at all
 *
 * A date-of-death figure is fixed by what the market did on a past date, so
 * `manualEntry` is absent for it and {@link fixedNote} is said instead. Offering
 * a toggle there and hard-wiring it to `No` would name a refresh that does not
 * happen, and offering it set to `Yes` would imply the reader had taken over
 * something Vale was otherwise driving. Its figures stay editable throughout,
 * because they always were.
 *
 * ## Commit, revert, and the Escape guard
 *
 * Committing on close is this package's popover contract (`address`, `name`,
 * `multiSelect`, `principalAndMembers` and `valueWithConfirmation` all do it) and
 * `revertedRef` is the same guard they all carry: Radix fires
 * `onOpenChange(false)` after `onEscapeKeyDown` for one keypress, so without it a
 * revert would be immediately followed by a commit of the reverted value.
 */

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/formatters/australian-formatters';
import { AttributeSegmentedBoolean } from './attribute-segmented-boolean';
import {
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import {
  ATTRIBUTE_NOT_RECORDED,
  attributePlaceholder
} from './attribute-placeholder';
import {
  AttributeCompositeField,
  AttributeCompositeMoneyField
} from './attribute-composite-fields';
import type { AttributeDraft, AttributeMarketFigure } from './attribute-types';
import { useAttributePopoverCommit } from './use-attribute-popover-commit';

/** Empty is both figures absent and Vale still driving, never `null`. */
const EMPTY: AttributeMarketFigure = { value: '', price: '', manual: false };

/**
 * **The row's read-out — the holding's value, or nothing yet.**
 *
 * Lifted out of the trigger's body and exported so the row's commit bridge
 * renders the identical string (VALE-1773): the bridge draws the just-committed
 * figure in the read-out for the frames between the editor closing and the
 * host's record prop catching up, and the reader is looking at this trigger's
 * output at the moment it closes. See `attribute-draft-display.ts`.
 */
export function formatMarketFigureSummary(
  value: AttributeMarketFigure | null
): string | null {
  const draft = value ?? EMPTY;
  return draft.value.trim() ? formatCurrency(Number(draft.value)) : null;
}

export function AttributeMarketFigureEditor({
  label,
  valueLabel,
  priceLabel,
  value,
  provenance,
  derivation,
  deriveValue,
  manualEntry,
  fixedNote,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  valueLabel: string;
  priceLabel: string;
  value: AttributeMarketFigure | null;
  provenance?: string[];
  derivation?: string | null;
  deriveValue?: (price: string) => number | undefined;
  manualEntry?: { label: string; note: string };
  fixedNote?: string;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const draft = value ?? EMPTY;
  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: draft,
    onRevert,
    onCommit
  });

  /*
    Absent `manualEntry` means the figure never resyncs, so its fields are always
    the reader's — see the header. Present, the answer is the gate.
  */
  const editable = manualEntry ? draft.manual : true;

  /**
   * A new price draft, with the value recomputed from it.
   *
   * `undefined` from `deriveValue` (no unit count on file) leaves the value
   * exactly as the reader last set it rather than blanking it: a holding whose
   * share count has not been entered yet still has a total somebody typed, and
   * clearing it because the arithmetic is unavailable would destroy data to
   * report an absence.
   */
  const withPrice = (raw: string): AttributeMarketFigure => {
    const derived = deriveValue?.(raw);
    return {
      ...draft,
      price: raw,
      value: derived === undefined ? draft.value : String(derived)
    };
  };

  const summary = formatMarketFigureSummary(draft);

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        {/*
          Left-aligned and cell-width, matching the read-out it replaces, so the
          figure does not jump sideways as the popover opens. Bare: the cell draws
          the fill and the ring.
        */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground min-w-0 flex-1 truncate rounded-md text-left tabular-nums',
            ATTRIBUTE_EDITOR_BARE
          )}
        >
          {summary ?? attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Keep off the viewport edge, like every other popover kind here. This
        // row sits in the drawer's right-hand column, which is where the panel
        // would otherwise open flush against the window.
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        className={cn(ATTRIBUTE_POPOVER_PANEL, ATTRIBUTE_COMPOSITE_PARTS)}
        {...popoverCommit.content}
      >
        {/*
          Where the number came from, first and read-only. Rendered only when
          there is something to say: a delisted holding has no market price and no
          fetch date, and an empty block above the fields would read as a value
          that failed to load rather than as one Vale never had.
        */}
        {provenance && provenance.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {provenance.map((line) => (
              <p
                key={line}
                className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}
              >
                {line}
              </p>
            ))}
          </div>
        ) : null}

        {manualEntry ? (
          <AttributeCompositeField label={manualEntry.label}>
            <AttributeSegmentedBoolean
              value={draft.manual}
              ariaLabel={manualEntry.label}
              onValueChange={(next) =>
                /*
                  Answering `No` hands the figure back to Vale, and deliberately
                  does NOT clear it here: the record keeps whatever is on it until
                  the market data actually arrives, so the row never flashes empty
                  between the answer and the next fetch. The spec's `toPatch` owns
                  what `No` restores, because only it can see the fetched figures.
                */
                onDraft({ ...draft, manual: next })
              }
            />
          </AttributeCompositeField>
        ) : null}

        {editable ? (
          <>
            <AttributeCompositeMoneyField
              label={priceLabel}
              value={draft.price}
              autoFocus
              onChange={(raw) => onDraft(withPrice(raw))}
            />
            <AttributeCompositeMoneyField
              label={valueLabel}
              value={draft.value}
              onChange={(raw) => onDraft({ ...draft, value: raw })}
            />
          </>
        ) : (
          /*
            Vale owns the figure, so it reads out rather than opening. Not a
            disabled input: a control that looks live and refuses the click is
            worse than a value that plainly is not one.
          */
          <AttributeCompositeField label={valueLabel}>
            <p
              className={cn(
                ATTRIBUTE_VALUE_TEXT,
                ATTRIBUTE_LEADING,
                'py-0.5 tabular-nums'
              )}
            >
              {summary ?? ATTRIBUTE_NOT_RECORDED}
            </p>
          </AttributeCompositeField>
        )}

        {derivation ? (
          <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            {derivation}
          </span>
        ) : null}

        {manualEntry ? (
          <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            {manualEntry.note}
          </p>
        ) : fixedNote ? (
          <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            {fixedNote}
          </p>
        ) : null}
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
